import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StreakApi } from 'src/api';
import { StreakResponse } from 'src/api/streak/types';

const DEFAULT_REFETCH_INTERVAL = 500;
const DEFAULT_MAX_RETRIES = 3;

export const useStreakData = (params: {
  enabled?: boolean;
  languagePairId: number | null | undefined;
  waitForAccomplished?: boolean;
  maxRetries?: number;
  refetchInterval?: number;
}) => {
  const { languagePairId, waitForAccomplished } = params;
  const maxRetries = params.maxRetries || DEFAULT_MAX_RETRIES;
  const refetchInterval = params.refetchInterval || DEFAULT_REFETCH_INTERVAL;
  const enabledFromParam = params.enabled || !('enabled' in params);

  const [retries, setRetries] = useState(0);
  const streakDataRef = useRef<StreakResponse | undefined>(undefined);
  const isWaitingForAccomplished =
    !streakDataRef.current ||
    (waitForAccomplished && !streakDataRef.current.currentStreak.accomplishedToday);

  const requestEnabled = Boolean(
    enabledFromParam && languagePairId && retries < maxRetries && isWaitingForAccomplished,
  );

  const { data: fetchedStreakData } = useQuery({
    queryKey: ['streak', languagePairId],
    queryFn: async () => {
      try {
        const response = await StreakApi.getStreak(languagePairId as number);
        streakDataRef.current = response;
        return response;
      } finally {
        setRetries(r => r + 1);
      }
    },
    enabled: requestEnabled,
    refetchInterval: refetchInterval,
  });

  const streakData = isWaitingForAccomplished ? undefined : fetchedStreakData;
  return { isLoading: requestEnabled, streakData };
};
